import { PermanentObject, QuerySpecification, StorageServiceClient } from "./clients/StorageServiceClient"

type Defined<T> = Exclude<T, undefined>

export class QueryIterator<RowType extends PermanentObject> {
    // This currently fetches all data and then returns the results
    // This is not really necessary, it could stream data instead and should. But in
    // order to save time we use it like this for now
    // TODO: Reimplement so it streams data to the user instead of fetching it all at once
    private results: RowType[] = []
    private first: boolean = true
    private count: number = 0
    private allocator: { new (): RowType }
    private querySpecification: QuerySpecification
    private storageService: StorageServiceClient
    private writable: boolean
    //private meta: ResultMetaData; // Not needed?
    constructor(
        allocator: { new (): RowType },
        querySpecification: QuerySpecification,
        writable: boolean,
        storageContext: StorageServiceClient,
    ) {
        this.allocator = allocator
        this.querySpecification = querySpecification
        this.storageService = storageContext
        this.writable = writable
    }

    [Symbol.asyncIterator](): QueryIterator<RowType> {
        return this
    }

    /**
     * map version of for await.
     *
     * instead of:
     *
     *      const array = []
     *
     *      for await (const item of queryIterator) {
     *          array.push(processItemData(item))
     *      }
     *
     *
     * uses this method as:
     *      const array = await queryIterator.awaitMap(processItemData)
     */
    async awaitMap<T>(mapper: (row: RowType) => T): Promise<T[]> {
        const result: T[] = []
        for await (const item of this) {
            result.push(mapper(item))
        }

        return result
    }

    async awaitReturn(): Promise<RowType[]> {
        return await this.awaitMap(row => row)
    }

    /**
     * awaitMap that only returns elements for where the mapper did NOT return undefined
     */
    async awaitMapDefined<T>(mapper: (row: RowType) => Promise<T>): Promise<Defined<T>[]> {
        const result = await this.awaitMap(mapper)

        return result.filter(element => element !== undefined) as Defined<T>[]
    }

    async next(): Promise<IteratorResult<RowType>> {
        if (this.first) {
            this.first = false

            await this.storageService.QueryCallback<RowType>(
                this.allocator,
                this.querySpecification,
                async (meta, row) => {
                    this.results.push(row)
                    this.count++
                },
            )
        }

        const item = this.results.shift()

        return item === undefined ? { value: new this.allocator(), done: true } : { value: item, done: false }
    }
}
